import { useIntl } from 'react-intl';

import {
    GENDER_OPTIONS,
    HELP_ASSISTANCE_OPTIONS,
    POLITICAL_PARTY_OPTIONS,
    RACE_OPTIONS,
    REGISTRATION_REASON_OPTIONS,
    SUFFIX_OPTIONS,
    UNIT_TYPES_OPTIONS,
    US_STATES_OPTIONS,
    YES_NO_OPTIONS,
} from './constants/voterRegistrationConstants';
import { useAppDispatch, useAppSelector, useModal } from '@hooks/index';
import {
    getVoterRegistrationInitData,
    saveVoterRegistrationData,
    fetchCountiesForZipcode,
    updateVoterRegistrationFlagData,
} from '@actions/individualPortal/voterRegistration/voterRegistrationActionCreators';
import { environmentSelector } from '@selectors/commonSelectors';
import { configs } from '@constants/configs/configs';
import { STATES_TYPE } from '@constants/configs/states';

const useVoterRegistration = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const { stateCode } = useAppSelector(environmentSelector);

    const fetchInitData = async (applicationId: string) => {
        const response = await getVoterRegistrationInitData()(applicationId, dispatch);
        return response;
    };

    const saveVoterRegistration = async (formData: FormData, caseNumber: string) => {
        const response = await saveVoterRegistrationData()(formData, caseNumber, dispatch);
        if (response?.message === 'success') {
            openModal('VoterRegistrationSuccessModal', { applicationId: response?.applicationId });
        } else {
            openModal('VoterRegistrationFailureModal', {
                isRetryAllowed: response?.message?.toLowerCase() === 'retry' || false,
            });
        }
    };

    const updateVoterRegistrationFlag = async (applicationId: string, isVoterRegistration: boolean) => {
        const response = await updateVoterRegistrationFlagData()(applicationId, isVoterRegistration, dispatch);
        if (response?.message === 'success') {
            return true;
        }

        return false;
    };

    const getCounties = async (stateCode: STATES_TYPE, postalCode: string) => {
        const response = await fetchCountiesForZipcode()(stateCode, postalCode, dispatch);
        return Promise.resolve(response);
    };

    const localizeOptions = (arr: any[]) =>
        arr.map(item => ({
            value: item.value,
            text: intl.formatMessage({ id: item.text, defaultMessage: item.text }),
            ...(item.optionId ? { optionId: item.optionId } : {}),
        }));

    const VOTER_REGISTRATION_CHOICES = {
        SUFFIX: localizeOptions(SUFFIX_OPTIONS),
        YES_NO: localizeOptions(YES_NO_OPTIONS),
        REGISTRATION_REASON: localizeOptions(REGISTRATION_REASON_OPTIONS),
        GENDER: localizeOptions(GENDER_OPTIONS),
        RACE: localizeOptions(RACE_OPTIONS),
        UNIT_TYPES: localizeOptions(UNIT_TYPES_OPTIONS),
        POLITICAL_PARTY: localizeOptions(POLITICAL_PARTY_OPTIONS),
        HELP_ASSISTANCE: localizeOptions(HELP_ASSISTANCE_OPTIONS),
        US_STATES: localizeOptions(US_STATES_OPTIONS),
    };

    const stateElectionOfficeLink = configs.endpoints.externalUrls.stateElectionOfficeLink[stateCode] ?? '';
    const voterRegistrationFormPath = configs.assets[stateCode].voterRegistrationForm ?? '';

    const DROPDOWN_DEFAULT_OPTION = {
        value: '',
        text: intl.formatMessage({ id: 'voterRegistration.form.default' }),
    };

    return {
        VOTER_REGISTRATION_CHOICES,
        DROPDOWN_DEFAULT_OPTION,
        fetchInitData,
        saveVoterRegistration,
        getCounties,
        stateElectionOfficeLink,
        updateVoterRegistrationFlag,
        voterRegistrationFormPath,
    };
};

export default useVoterRegistration;
